import React, { useState } from 'react';
import { Button, Alert, Form } from 'antd';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentService } from '../../services/paymentService';

interface StripePaymentFormProps {
  amount: number;
  useAffirm?: boolean;
  onSuccess: () => void;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  amount,
  useAffirm = false,
  onSuccess
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elementReady, setElementReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system not initialized. Please wait and try again.');
      return;
    }

    if (!elementReady) {
      setError('Payment form is still loading. Please wait a moment and try again.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/return`,
        },
        redirect: 'if_required'
      });

      if (submitError) {
        setError(submitError.message || 'Payment failed. Please try again.');
        setProcessing(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded - confirm on backend
        try {
          await paymentService.confirmPaymentIntent(paymentIntent.id);
          console.log('✓ Payment confirmed on backend');
        } catch (confirmError) {
          console.warn('Payment succeeded but backend confirmation failed (webhook will handle it):', confirmError);
          // Don't show error to user - payment still succeeded on Stripe's side
        }
        onSuccess();
      } else {
        setError('Payment status unclear. Please check your payment status or contact support.');
        setProcessing(false);
      }
    } catch (err: any) {
      setError(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <Form onSubmitCapture={handleSubmit}>
      {error && (
        <Alert
          message="Payment Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {useAffirm && (
        <Alert
          message="Affirm Financing"
          description="Get approved instantly with a soft credit check that won't affect your credit score. Choose from 3, 6, or 12-month payment plans."
          type="info"
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <div style={{ marginBottom: 24 }}>
        <PaymentElement
          onReady={() => setElementReady(true)}
          onLoadError={(error) => setError('Failed to load payment form. Please refresh and try again.')}
        />
      </div>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        loading={processing}
        disabled={!stripe || !elementReady || processing}
        block
        style={{ pointerEvents: processing ? 'none' : 'auto' }}
      >
        {processing ? 'Processing...' : !elementReady ? 'Loading payment form...' : `Pay $${amount.toFixed(2)}`}
      </Button>

      <div style={{ marginTop: 12, textAlign: 'center', color: '#8c8c8c', fontSize: '12px' }}>
        <p>Payment securely processed by Stripe</p>
      </div>
    </Form>
  );
};

export default StripePaymentForm;
